import React from 'react';
import { DefaultButton, MessageBar, MessageBarType, PrimaryButton, Stack, TextField } from '@fluentui/react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ApplicationContext } from '../../contexts/ApplicationContext';
import { IBank, INetsSettings } from '../../models';
import useObject from '../../hooks/useObject';
import LoadingIndicator from '../../components/LoadingIndicator';
import ErrorDialog from '../../components/ErrorDialog';

interface INetsProps
{

}

const onlyDigits = (value: string | undefined): string => (value ?? "").replace(/\D/g, "");

export const Nets: React.FunctionComponent<INetsProps> = (props: React.PropsWithChildren<INetsProps>) => {
    const navigate: NavigateFunction = useNavigate();
    const { Services } = React.useContext(ApplicationContext);
    const { value: entity, updateValue: updateEntity, setValue, hasChanges } = useObject<INetsSettings>({} as INetsSettings);
    const [banks, setBanks] = React.useState<IBank[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saved, setSaved] = React.useState<boolean>(false);
    const [errorDialogText, setErrorDialogText] = React.useState<string>('');
    const [showErrorDialog, setShowErrorDialog] = React.useState<boolean>(false);

    const openErrorDialog = (message: string) => {
        setErrorDialogText(message);
        setShowErrorDialog(true);
    };

    React.useEffect(() => {
        Fetch();
    }, []);

    const Fetch = async () => {
        setIsLoading(true);
        try {
            const [settings, allBanks] = await Promise.all([Services!.Company.GetNetsSettings(), Services!.Bank.GetAll()]);
            setValue(settings);
            setBanks(allBanks);
        } catch (e) {
            openErrorDialog((e as Error).message);
        }
        setIsLoading(false);
    }

    // Feltlængderne følger positionerne i NETS-filen
    const errors = {
        dataSupplier: (entity.dataSupplier ?? "").length > 20 ? "Højst 20 tegn" : undefined,
        dataSupplierCVR: entity.dataSupplierCVR && entity.dataSupplierCVR.length !== 8 ? "CVR-nr. skal være 8 cifre" : undefined,
        bankRegNo: entity.bankRegNo !== undefined && entity.bankRegNo !== null && banks.length > 0 && !banks.some(b => b.regNo === entity.bankRegNo) ? "Reg.nr. findes ikke under Banker" : undefined,
        postId: (entity.postId ?? "").length > 20 ? "Højst 20 tegn" : undefined,
    };
    const isValid = Object.values(errors).every(e => e === undefined);
    const bankName = banks.find(b => b.regNo === entity.bankRegNo)?.name;

    const Save = async () => {
        setIsSaving(true);
        const result = await Services?.Company.UpdateNetsSettings(entity);
        setIsSaving(false);

        if(result === "OK")
        {
            setSaved(true);
            Fetch();
        } else
        {
            openErrorDialog(result as string);
        }
    }

    const update = (change: Partial<INetsSettings>) => {
        setSaved(false);
        updateEntity(change);
    }

    if(isLoading) return <LoadingIndicator label="Henter NETS-opsætning..." />

    return <div>
                <h3>NETS</h3>
                <Stack tokens={{ childrenGap: 10 }} styles={{ root: { maxWidth: 600 } }}>
                    <h4 style={{ marginBottom: 0 }}>Dataleverandør</h4>
                    <TextField label="Dataleverandør" value={entity.dataSupplier ?? ""} maxLength={20} errorMessage={errors.dataSupplier} description="Navnet i filens startrecord (OS1), højst 20 tegn" onChange={(ev, newValue) => update({ dataSupplier: newValue })} />
                    <TextField label="Dataleverandør CVR-nr." value={entity.dataSupplierCVR ?? ""} maxLength={8} errorMessage={errors.dataSupplierCVR} onChange={(ev, newValue) => update({ dataSupplierCVR: onlyDigits(newValue) })} />

                    <h4 style={{ marginBottom: 0 }}>Betalende konto</h4>
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <TextField label="Reg.nr." value={entity.bankRegNo !== undefined && entity.bankRegNo !== null ? entity.bankRegNo + "" : ""} maxLength={4} errorMessage={errors.bankRegNo} description={bankName} styles={{ root: { width: 140 } }} onChange={(ev, newValue) => { const digits = onlyDigits(newValue); update({ bankRegNo: digits !== "" ? parseInt(digits) : undefined }); }} />
                        <TextField label="Kontonr." value={entity.bankAccountNo ?? ""} maxLength={10} description="Fyldes op med foranstillede nuller ved gem" styles={{ root: { flexGrow: 1 } }} onChange={(ev, newValue) => update({ bankAccountNo: onlyDigits(newValue) })} />
                    </Stack>

                    <h4 style={{ marginBottom: 0 }}>Overførsel</h4>
                    <TextField label="Posteringstekst" value={entity.postId ?? ""} maxLength={20} errorMessage={errors.postId} description="Teksten modtageren ser på kontoudtoget, højst 20 tegn" onChange={(ev, newValue) => update({ postId: newValue })} />
                    <TextField label="Modtager-identifikation" value={entity.receiverId ?? ""} description="Vises på følgebrevet" onChange={(ev, newValue) => update({ receiverId: newValue })} />

                    {saved && <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSaved(false)}>NETS-opsætningen er gemt</MessageBar>}

                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton text={isSaving ? "Gemmer..." : "Gem ændringer"} disabled={!hasChanges || !isValid || isSaving} onClick={() => Save()} />
                        <DefaultButton text="Tilbage" onClick={() => { navigate("/indstillinger"); }} />
                    </Stack>
                </Stack>
                <ErrorDialog
                    isOpen={showErrorDialog}
                    errorText={errorDialogText}
                    onDismiss={() => setShowErrorDialog(false)}
                />
           </div>
}

export default Nets;
